import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from "@material-ui/core";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import { toast } from "react-toastify";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const initialPipelineForm = {
  id: null,
  name: "",
  color: "#1976d2",
  sortOrder: 0
};

const initialStageForm = {
  id: null,
  pipelineId: "",
  name: "",
  color: "#1976d2",
  sortOrder: 0
};

const sortByOrder = list =>
  [...(list || [])].sort(
    (a, b) => Number(a.sortOrder || 0) - Number(b.sortOrder || 0)
  );

// Opcoes de ordem: 0..(quantidade), garantindo que o valor atual apareca.
const orderOptions = (count, current) => {
  const max = Math.max(Number(count) || 1, Number(current || 0) + 1);
  return Array.from({ length: max }, (_, i) => i);
};

const KanbanStages = () => {
  const [pipelines, setPipelines] = useState([]);
  const [selectedPipelineId, setSelectedPipelineId] = useState("");
  const [pipelineDialogOpen, setPipelineDialogOpen] = useState(false);
  const [stageDialogOpen, setStageDialogOpen] = useState(false);
  const [pipelineForm, setPipelineForm] = useState(initialPipelineForm);
  const [stageForm, setStageForm] = useState(initialStageForm);

  const loadPipelines = async () => {
    try {
      const { data } = await api.get("/kanban-pipelines");
      setPipelines(data);
      if (!selectedPipelineId && data.length) {
        setSelectedPipelineId(String(data[0].id));
      }
    } catch (err) {
      toastError(err);
    }
  };

  useEffect(() => {
    loadPipelines();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedPipeline = useMemo(
    () => pipelines.find(item => String(item.id) === String(selectedPipelineId)),
    [pipelines, selectedPipelineId]
  );

  const sortedPipelines = useMemo(() => sortByOrder(pipelines), [pipelines]);
  const sortedStages = useMemo(
    () => sortByOrder(selectedPipeline?.stages || []),
    [selectedPipeline]
  );

  // ---- edicao inline: pipelines ----
  const updatePipelineLocal = (id, field, value) =>
    setPipelines(prev =>
      prev.map(p => (p.id === id ? { ...p, [field]: value } : p))
    );

  const persistPipeline = async (id, patch = {}) => {
    const p = pipelines.find(x => x.id === id);
    if (!p) return;
    const merged = { ...p, ...patch };
    try {
      await api.put(`/kanban-pipelines/${id}`, {
        name: merged.name,
        color: merged.color,
        sortOrder: Number(merged.sortOrder || 0)
      });
    } catch (err) {
      toastError(err);
    }
  };

  // ---- edicao inline: estagios ----
  const updateStageLocal = (stageId, field, value) =>
    setPipelines(prev =>
      prev.map(p =>
        p.id === selectedPipeline?.id
          ? {
              ...p,
              stages: (p.stages || []).map(s =>
                s.id === stageId ? { ...s, [field]: value } : s
              )
            }
          : p
      )
    );

  const persistStage = async (stageId, patch = {}) => {
    const stage = (selectedPipeline?.stages || []).find(s => s.id === stageId);
    if (!stage) return;
    const merged = { ...stage, ...patch };
    try {
      await api.put(`/kanban-stages/${stageId}`, {
        pipelineId: Number(merged.pipelineId),
        name: merged.name,
        color: merged.color,
        sortOrder: Number(merged.sortOrder || 0)
      });
    } catch (err) {
      toastError(err);
    }
  };

  const handleClosePipelineDialog = () => {
    setPipelineForm(initialPipelineForm);
    setPipelineDialogOpen(false);
  };

  const handleCloseStageDialog = () => {
    setStageForm(initialStageForm);
    setStageDialogOpen(false);
  };

  const handleSubmitPipeline = async event => {
    event.preventDefault();
    try {
      await api.post("/kanban-pipelines", {
        ...pipelineForm,
        sortOrder: Number(pipelineForm.sortOrder || 0)
      });
      toast.success("Pipeline criado");
      handleClosePipelineDialog();
      loadPipelines();
    } catch (err) {
      toastError(err);
    }
  };

  const handleSubmitStage = async event => {
    event.preventDefault();
    try {
      await api.post("/kanban-stages", {
        ...stageForm,
        pipelineId: Number(stageForm.pipelineId),
        sortOrder: Number(stageForm.sortOrder || 0)
      });
      toast.success("Estagio criado");
      handleCloseStageDialog();
      loadPipelines();
    } catch (err) {
      toastError(err);
    }
  };

  const handleDeletePipeline = async pipelineId => {
    try {
      await api.delete(`/kanban-pipelines/${pipelineId}`);
      toast.success("Pipeline removido");
      if (String(selectedPipelineId) === String(pipelineId)) {
        setSelectedPipelineId("");
      }
      loadPipelines();
    } catch (err) {
      toastError(err);
    }
  };

  const handleDeleteStage = async stageId => {
    try {
      await api.delete(`/kanban-stages/${stageId}`);
      toast.success("Estagio removido");
      loadPipelines();
    } catch (err) {
      toastError(err);
    }
  };

  const colorCell = (value, onChange) => (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <input
        type="color"
        value={value || "#1976d2"}
        onChange={onChange}
        style={{
          width: 34,
          height: 26,
          padding: 0,
          border: "1px solid #ccc",
          borderRadius: 4,
          background: "none",
          cursor: "pointer"
        }}
      />
      <span style={{ fontSize: 12, color: "#64748b" }}>{value}</span>
    </div>
  );

  return (
    <MainContainer>
      <Dialog
        open={pipelineDialogOpen}
        onClose={handleClosePipelineDialog}
        maxWidth="sm"
        fullWidth
      >
        <form onSubmit={handleSubmitPipeline}>
          <DialogTitle>Novo pipeline</DialogTitle>
          <DialogContent dividers>
            <TextField
              label="Nome"
              value={pipelineForm.name}
              onChange={event =>
                setPipelineForm(prev => ({ ...prev, name: event.target.value }))
              }
              variant="outlined"
              margin="dense"
              fullWidth
              required
            />
            <TextField
              label="Cor"
              type="color"
              value={pipelineForm.color}
              onChange={event =>
                setPipelineForm(prev => ({ ...prev, color: event.target.value }))
              }
              variant="outlined"
              margin="dense"
              fullWidth
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleClosePipelineDialog}
              color="secondary"
              variant="outlined"
            >
              Cancelar
            </Button>
            <Button type="submit" color="primary" variant="contained">
              Criar
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog
        open={stageDialogOpen}
        onClose={handleCloseStageDialog}
        maxWidth="sm"
        fullWidth
      >
        <form onSubmit={handleSubmitStage}>
          <DialogTitle>Novo estagio</DialogTitle>
          <DialogContent dividers>
            <TextField
              select
              SelectProps={{ native: true }}
              label="Pipeline"
              value={stageForm.pipelineId}
              onChange={event =>
                setStageForm(prev => ({ ...prev, pipelineId: event.target.value }))
              }
              variant="outlined"
              margin="dense"
              fullWidth
              required
            >
              <option value="">Selecione</option>
              {pipelines.map(pipeline => (
                <option key={pipeline.id} value={pipeline.id}>
                  {pipeline.name}
                </option>
              ))}
            </TextField>
            <TextField
              label="Nome"
              value={stageForm.name}
              onChange={event =>
                setStageForm(prev => ({ ...prev, name: event.target.value }))
              }
              variant="outlined"
              margin="dense"
              fullWidth
              required
            />
            <TextField
              label="Cor"
              type="color"
              value={stageForm.color}
              onChange={event =>
                setStageForm(prev => ({ ...prev, color: event.target.value }))
              }
              variant="outlined"
              margin="dense"
              fullWidth
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleCloseStageDialog}
              color="secondary"
              variant="outlined"
            >
              Cancelar
            </Button>
            <Button type="submit" color="primary" variant="contained">
              Criar
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <MainHeader>
        <Title>Pipelines e Estagios</Title>
        <MainHeaderButtonsWrapper>
          <Button
            color="primary"
            variant="contained"
            onClick={() => {
              setPipelineForm(initialPipelineForm);
              setPipelineDialogOpen(true);
            }}
            style={{ marginRight: 8 }}
          >
            Novo pipeline
          </Button>
          <Button
            color="primary"
            variant="outlined"
            onClick={() => {
              setStageForm({
                ...initialStageForm,
                pipelineId: selectedPipelineId || ""
              });
              setStageDialogOpen(true);
            }}
            disabled={!selectedPipelineId}
          >
            Novo estagio
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Grid container spacing={2}>
        <Grid item xs={12} md={5}>
          <Paper style={{ padding: 16 }}>
            <Typography variant="subtitle1" gutterBottom>
              Pipelines
            </Typography>
            <Typography
              variant="caption"
              color="textSecondary"
              style={{ display: "block", marginBottom: 8 }}
            >
              Clique para selecionar. Edite nome, cor e ordem direto na linha.
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Nome</TableCell>
                  <TableCell>Cor</TableCell>
                  <TableCell>Ordem</TableCell>
                  <TableCell align="right">Acoes</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedPipelines.map(pipeline => (
                  <TableRow
                    key={pipeline.id}
                    hover
                    selected={
                      String(selectedPipelineId) === String(pipeline.id)
                    }
                    onClick={() => setSelectedPipelineId(String(pipeline.id))}
                    style={{ cursor: "pointer" }}
                  >
                    <TableCell onClick={e => e.stopPropagation()}>
                      <TextField
                        value={pipeline.name}
                        onChange={e =>
                          updatePipelineLocal(pipeline.id, "name", e.target.value)
                        }
                        onBlur={() => persistPipeline(pipeline.id)}
                        margin="dense"
                        fullWidth
                      />
                    </TableCell>
                    <TableCell onClick={e => e.stopPropagation()}>
                      {colorCell(pipeline.color, e => {
                        updatePipelineLocal(pipeline.id, "color", e.target.value);
                        persistPipeline(pipeline.id, { color: e.target.value });
                      })}
                    </TableCell>
                    <TableCell onClick={e => e.stopPropagation()}>
                      <TextField
                        select
                        SelectProps={{ native: true }}
                        value={Number(pipeline.sortOrder || 0)}
                        onChange={e => {
                          updatePipelineLocal(
                            pipeline.id,
                            "sortOrder",
                            Number(e.target.value)
                          );
                          persistPipeline(pipeline.id, {
                            sortOrder: Number(e.target.value)
                          });
                        }}
                        margin="dense"
                      >
                        {orderOptions(
                          pipelines.length,
                          pipeline.sortOrder
                        ).map(n => (
                          <option key={n} value={n}>
                            {n}
                          </option>
                        ))}
                      </TextField>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={event => {
                          event.stopPropagation();
                          handleDeletePipeline(pipeline.id);
                        }}
                      >
                        <DeleteOutlineIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </Grid>

        <Grid item xs={12} md={7}>
          <Paper style={{ padding: 16 }}>
            <Typography variant="subtitle1" gutterBottom>
              {selectedPipeline
                ? `Estagios de ${selectedPipeline.name}`
                : "Selecione um pipeline"}
            </Typography>
            <Divider style={{ marginBottom: 16 }} />
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Nome</TableCell>
                  <TableCell>Cor</TableCell>
                  <TableCell>Ordem</TableCell>
                  <TableCell align="right">Acoes</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedStages.map(stage => (
                  <TableRow key={stage.id}>
                    <TableCell>
                      <TextField
                        value={stage.name}
                        onChange={e =>
                          updateStageLocal(stage.id, "name", e.target.value)
                        }
                        onBlur={() => persistStage(stage.id)}
                        margin="dense"
                        fullWidth
                      />
                    </TableCell>
                    <TableCell>
                      {colorCell(stage.color, e => {
                        updateStageLocal(stage.id, "color", e.target.value);
                        persistStage(stage.id, { color: e.target.value });
                      })}
                    </TableCell>
                    <TableCell>
                      <TextField
                        select
                        SelectProps={{ native: true }}
                        value={Number(stage.sortOrder || 0)}
                        onChange={e => {
                          updateStageLocal(
                            stage.id,
                            "sortOrder",
                            Number(e.target.value)
                          );
                          persistStage(stage.id, {
                            sortOrder: Number(e.target.value)
                          });
                        }}
                        margin="dense"
                      >
                        {orderOptions(
                          (selectedPipeline?.stages || []).length,
                          stage.sortOrder
                        ).map(n => (
                          <option key={n} value={n}>
                            {n}
                          </option>
                        ))}
                      </TextField>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteStage(stage.id)}
                      >
                        <DeleteOutlineIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </Grid>
      </Grid>
    </MainContainer>
  );
};

export default KanbanStages;
